/**
 * `GET /api/payments/seller-provider?sellerId=…` — rail-availability gate.
 *
 * Returns which payment providers a seller is currently accepting. The mobile
 * client today (`apps/mobile/lib/api-paypal.ts::fetchSellerPaymentProviders`)
 * runs a 4-layer defensive parser — `providers` → `chosen` → legacy `provider`
 * → deep-scan body for any provider string. That fallback exists precisely
 * because the route is untyped. Pinning the schema here lets mobile drop the
 * deep-scan fallback once typed-client work (RELIABILITY_ROADMAP §4) lands.
 *
 * Backward-compat note: `provider` is the legacy field shipped before the
 * multi-provider router. The route still emits it (mirrors `chosen` or
 * defaults to `"paypal"`). Mobile reads it as the third fallback. Don't drop
 * it without coordinating both clients.
 *
 * @see app/api/payments/seller-provider/route.ts — handler
 * @see apps/mobile/lib/api-paypal.ts::fetchSellerPaymentProviders — defensive parser
 * @see lib/payments/types.ts ProviderName — server-side type (kept in sync)
 * @see .tondocs/payments/API_RESPONSE_AUDIT.md §5
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * Canonical provider name on the wire. Mirrors `lib/payments/types.ts::ProviderName`
 * and `apps/mobile/lib/api-paypal.ts::PaymentProviderName`. Three independent
 * declarations existed pre-contract; this one is now the source of truth.
 * Adding a third rail (e.g. "klarna") happens here first — the union extension
 * forces all downstream switches to fail compile via `never` probes
 * ([[feedback_left_fix_audit_downstream_branches]]).
 */
export type PaymentProviderName = "paypal" | "stripe";

export const PAYMENT_PROVIDER_NAMES = ["paypal", "stripe"] as const;

// Same pattern as CHECKOUT_ITEM_CONTEXT_KINDS in checkout-intent.ts — keep the
// array and union provably aligned so a new rail can't slip past the loop
// without being added to both.
type _AssertProvidersCoverUnion =
  PaymentProviderName extends (typeof PAYMENT_PROVIDER_NAMES)[number] ? true : never;
type _AssertNoExtraProviders =
  (typeof PAYMENT_PROVIDER_NAMES)[number] extends PaymentProviderName ? true : never;
const _providerPinCoverUnion: _AssertProvidersCoverUnion = true;
const _providerPinNoExtras: _AssertNoExtraProviders = true;
void _providerPinCoverUnion;
void _providerPinNoExtras;

export const PaymentProviderNameSchema = pinned<PaymentProviderName>()(
  z.enum(PAYMENT_PROVIDER_NAMES),
);

export interface SellerProviderResponse {
  /** Backward-compat — mirrors `chosen` or `"paypal"` as the historical default. */
  provider: PaymentProviderName;
  /** Ordered list of providers the seller accepts. First entry == `chosen`. */
  providers: PaymentProviderName[];
  /** First entry of `providers`, or `null` when the seller accepts no rails. */
  chosen: PaymentProviderName | null;
}

export const SellerProviderResponseSchema = pinned<SellerProviderResponse>()(
  z.object({
    provider: PaymentProviderNameSchema,
    providers: z.array(PaymentProviderNameSchema),
    chosen: PaymentProviderNameSchema.nullable(),
  }),
);
